import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type POSOrderTypeKey = 'dine_in' | 'takeaway' | 'glovo'
export type POSInputMode = 'laptop' | 'touchscreen'

export interface POSOrderTypeOption {
  key: POSOrderTypeKey
  label: string
  enabled: boolean
}

export interface POSRestaurantPreferences {
  orderTypeToggleEnabled: boolean
  orderTypeOptions: POSOrderTypeOption[]
}

export const DEFAULT_POS_ORDER_TYPE_OPTIONS: POSOrderTypeOption[] = [
  { key: 'dine_in', label: 'À table', enabled: true },
  { key: 'takeaway', label: 'Emporté', enabled: true },
  { key: 'glovo', label: 'Glovo', enabled: true },
]

export const DEFAULT_POS_RESTAURANT_PREFERENCES: POSRestaurantPreferences = {
  orderTypeToggleEnabled: false,
  orderTypeOptions: DEFAULT_POS_ORDER_TYPE_OPTIONS,
}

interface POSPreferencesStore {
  coversEnabled: boolean
  inputMode: POSInputMode
  restaurantPreferences: Record<string, POSRestaurantPreferences>
  setCoversEnabled: (enabled: boolean) => void
  setInputMode: (mode: POSInputMode) => void
  setOrderTypeToggleEnabled: (restaurantId: string | null | undefined, enabled: boolean) => void
  setOrderTypeOption: (
    restaurantId: string | null | undefined,
    key: POSOrderTypeKey,
    patch: Partial<Pick<POSOrderTypeOption, 'label' | 'enabled'>>,
  ) => void
}

const prefKey = (restaurantId: string | null | undefined) => restaurantId || '__default__'

export function getPOSRestaurantPreferences(
  preferencesByRestaurant: Record<string, POSRestaurantPreferences>,
  restaurantId: string | null | undefined,
): POSRestaurantPreferences {
  const stored = preferencesByRestaurant[prefKey(restaurantId)]
  if (!stored) return DEFAULT_POS_RESTAURANT_PREFERENCES
  const storedByKey = new Map((stored.orderTypeOptions ?? []).map((option) => [option.key, option]))
  return {
    orderTypeToggleEnabled: stored.orderTypeToggleEnabled ?? DEFAULT_POS_RESTAURANT_PREFERENCES.orderTypeToggleEnabled,
    orderTypeOptions: DEFAULT_POS_ORDER_TYPE_OPTIONS.map((defaultOption) => ({
      ...defaultOption,
      ...(storedByKey.get(defaultOption.key) ?? {}),
    })),
  }
}

export const usePOSPreferencesStore = create<POSPreferencesStore>()(
  persist(
    (set) => ({
      coversEnabled: true,
      inputMode: 'laptop',
      restaurantPreferences: {},
      setCoversEnabled: (coversEnabled) => set({ coversEnabled }),
      setInputMode: (inputMode) => set({ inputMode }),
      setOrderTypeToggleEnabled: (restaurantId, enabled) => set((state) => {
        const key = prefKey(restaurantId)
        const current = getPOSRestaurantPreferences(state.restaurantPreferences, restaurantId)
        return {
          restaurantPreferences: {
            ...state.restaurantPreferences,
            [key]: { ...current, orderTypeToggleEnabled: enabled },
          },
        }
      }),
      setOrderTypeOption: (restaurantId, optionKey, patch) => set((state) => {
        const key = prefKey(restaurantId)
        const current = getPOSRestaurantPreferences(state.restaurantPreferences, restaurantId)
        return {
          restaurantPreferences: {
            ...state.restaurantPreferences,
            [key]: {
              ...current,
              orderTypeOptions: current.orderTypeOptions.map((option) =>
                option.key === optionKey ? { ...option, ...patch } : option,
              ),
            },
          },
        }
      }),
    }),
    {
      name: 'pos-preferences-store',
      partialize: (state) => ({
        coversEnabled: state.coversEnabled,
        inputMode: state.inputMode,
        restaurantPreferences: state.restaurantPreferences,
      }),
    },
  ),
)
